import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { licenseService } from '../services/licenseService';
import { LicensePlan } from '../types/license';

interface PublicLicensePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ComparisonRow = {
  category: string;
  feature: string;
  free: string;
  basic: string;
  standard: string;
  pro: string;
  categoryRowspan?: number;
  isFirstInCategory?: boolean;
  isLastCategory?: boolean;
};

const getPlanTypeName = (planType: string) => {
  switch (planType) {
    case 'free':
      return '프리';
    case 'basic':
      return '베이직';
    case 'standard':
      return '스탠다드';
    case 'pro':
      return '프로';
    default:
      return planType;
  }
};

const getPlanSubtitle = (planType: string): string | null => {
  switch (planType) {
    case 'free':
      return '무료 플랜';
    case 'basic':
      return '소규모 클래스';
    case 'standard':
      return '중규모 클래스';
    case 'pro':
      return '대규모/학교';
    default:
      return null;
  }
};

// LicensePage와 동일한 구성
const groupFeaturesByCategory = (planType: string): Array<{ category: string; features: string[] }> => {
  const categories: Array<{ category: string; features: string[] }> = [];
  const classBasicFeatures: string[] = [];
  if (planType === 'free' || planType === 'basic') classBasicFeatures.push('클래스 1개');
  else if (planType === 'standard') classBasicFeatures.push('클래스 2개');
  else if (planType === 'pro') classBasicFeatures.push('클래스 5개');
  if (planType === 'free' || planType === 'basic') classBasicFeatures.push('기본 1개 그룹');
  else if (planType === 'standard') classBasicFeatures.push('최대 2개 그룹');
  else if (planType === 'pro') classBasicFeatures.push('최대 15개 그룹');
  if (planType === 'standard' || planType === 'pro') classBasicFeatures.push('멤버 수 35명 (멤버 그룹별)');
  else classBasicFeatures.push('멤버 수 30명 (멤버 그룹별)');
  classBasicFeatures.push(planType !== 'free' ? '추가 프로필 세트 사용' : '');
  classBasicFeatures.push(planType === 'standard' || planType === 'pro' ? '아이템 카드 사용' : '');
  classBasicFeatures.push(planType === 'standard' || planType === 'pro' ? '멤버 개별 페이지 접근' : '');
  classBasicFeatures.push(
    planType === 'standard' || planType === 'pro' ? '스토리지 데이터 용량 100MB' : ''
  );
  classBasicFeatures.push(planType === 'pro' ? '클래스 설정 연동' : '');
  categories.push({ category: '클래스 기본', features: classBasicFeatures });

  const alarmFeatures: string[] = [];
  if (planType === 'free') alarmFeatures.push('알람 세트 1개');
  else if (planType === 'basic') alarmFeatures.push('알람 세트 3개');
  else if (planType === 'standard') alarmFeatures.push('알람 세트 5개');
  else alarmFeatures.push('알람 세트 10개');
  alarmFeatures.push(planType === 'free' ? '기본 알람만' : '모든 알람 종류');
  categories.push({ category: '알람', features: alarmFeatures });

  const assignmentFeatures: string[] = [];
  assignmentFeatures.push(planType === 'free' ? '과제 10개/월' : '과제 무제한');
  if (planType === 'free') assignmentFeatures.push('일반 과제만');
  else if (planType === 'basic') assignmentFeatures.push('일반, 루틴 과제');
  else assignmentFeatures.push('일반, 루틴, 프로젝트 과제');
  if (planType === 'standard' || planType === 'pro') {
    assignmentFeatures.push('개별 과제(이미지, 링크, 메세지) 제출 및 확인');
  } else {
    assignmentFeatures.push('');
  }
  categories.push({ category: '과제 관리', features: assignmentFeatures });

  const chatFeatures: string[] = [];
  chatFeatures.push(planType === 'free' || planType === 'basic' ? '클래스챗 사용 불가' : '클래스챗 사용 가능');
  if (planType === 'free' || planType === 'basic') chatFeatures.push('');
  else if (planType === 'standard') chatFeatures.push('문제 세트 10개');
  else if (planType === 'pro') chatFeatures.push('문제 세트 20개');
  if (planType === 'free' || planType === 'basic') chatFeatures.push('');
  else chatFeatures.push('문제 카드 30개 (세트당)');
  categories.push({ category: '클래스 챗', features: chatFeatures });

  const boardFeatures: string[] = [];
  boardFeatures.push(planType === 'standard' || planType === 'pro' ? '전체공지/개별공지 저장' : '');
  if (planType === 'free') boardFeatures.push('노트 카테고리 1개');
  else if (planType === 'basic') boardFeatures.push('노트 카테고리 2개');
  else boardFeatures.push('노트 카테고리 5개');
  categories.push({ category: '보드', features: boardFeatures });
  return categories;
};

const comparisonData = [
  { category: '클래스 기본', feature: '클래스 생성', free: '1개', basic: '1개', standard: '2개', pro: '5개' },
  { category: '클래스 기본', feature: '멤버 그룹 생성 수', free: '기본 1개 그룹', basic: '기본 1개 그룹', standard: '최대 2개 그룹', pro: '최대 15개 그룹' },
  { category: '클래스 기본', feature: '멤버 수 (멤버 그룹별)', free: '30명', basic: '30명', standard: '35명', pro: '35명' },
  { category: '클래스 기본', feature: '추가 프로필 세트 사용\n(프로필 카드)', free: '불가', basic: '가능', standard: '가능', pro: '가능' },
  { category: '클래스 기본', feature: '아이템 카드 사용\n(레벨업, 과제보상, 스토어 카드)', free: '불가', basic: '불가', standard: '가능', pro: '가능' },
  { category: '클래스 기본', feature: '멤버 개별 페이지 접근', free: '불가', basic: '불가', standard: '가능', pro: '가능' },
  {
    category: '클래스 기본',
    feature: '스토리지 데이터 용량\n(과제 제출 이미지, 퀴즈 이미지)',
    free: '-',
    basic: '-',
    standard: '100MB',
    pro: '100MB'
  },
  { category: '클래스 기본', feature: '클래스 설정 연동', free: '불가', basic: '불가', standard: '불가', pro: '가능' },
  { category: '알람', feature: '알람 세트', free: '1개', basic: '3개', standard: '5개', pro: '10개' },
  { category: '알람', feature: '알람 종류', free: '기본 알람', basic: '모든 알람', standard: '모든 알람', pro: '모든 알람' },
  { category: '과제 관리', feature: '과제 생성 수 (월별)', free: '10개', basic: '무제한', standard: '무제한', pro: '무제한' },
  { category: '과제 관리', feature: '이용가능 과제 타입', free: '일반', basic: '일반, 루틴', standard: '일반, 루틴, 프로젝트', pro: '일반, 루틴, 프로젝트' },
  {
    category: '과제 관리',
    feature: '멤버 개별 과제 제출',
    free: '-',
    basic: '-',
    standard: '이미지, 링크, 메세지',
    pro: '이미지, 링크, 메세지'
  },
  { category: '클래스 챗', feature: '클래스챗 사용여부', free: '불가', basic: '불가', standard: '가능', pro: '가능' },
  { category: '클래스 챗', feature: '문제 세트 수 (클래스당)', free: '-', basic: '-', standard: '10개', pro: '20개' },
  { category: '클래스 챗', feature: '문제 카드 수 (세트당)', free: '-', basic: '-', standard: '30개', pro: '30개' },
  { category: '보드', feature: '전체공지/개별공지 저장', free: '불가', basic: '불가', standard: '가능', pro: '가능' },
  { category: '보드', feature: '노트 카테고리 수', free: '1개', basic: '2개', standard: '5개', pro: '5개' },
];

const PublicLicensePlanModal: React.FC<PublicLicensePlanModalProps> = ({ isOpen, onClose }) => {
  const [plans, setPlans] = useState<LicensePlan[]>([]);
  const [selectedDuration, setSelectedDuration] = useState<1 | 12>(1);
  const [purchaseMode, setPurchaseMode] = useState<'single' | 'group'>('single');

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      const loadedPlans = await licenseService.getPlans();
      setPlans(loadedPlans);
    })();
  }, [isOpen]);

  const sortedPlansByDuration = useMemo(() => {
    const plansByDuration = plans.reduce((acc, plan) => {
      const key = plan.duration === 6 ? 1 : plan.duration;
      if (!acc[key]) acc[key] = [];
      acc[key].push(plan);
      return acc;
    }, {} as Record<1 | 12, LicensePlan[]>);
    const planTypeOrder: Array<'free' | 'basic' | 'standard' | 'pro'> = ['free', 'basic', 'standard', 'pro'];
    return Object.entries(plansByDuration).reduce((acc, [duration, list]) => {
      acc[parseInt(duration) as 1 | 12] = list.sort(
        (a, b) => planTypeOrder.indexOf(a.planType as any) - planTypeOrder.indexOf(b.planType as any)
      );
      return acc;
    }, {} as Record<1 | 12, LicensePlan[]>);
  }, [plans]);

  const groupedData = useMemo<ComparisonRow[]>(() => {
    const out: ComparisonRow[] = [];
    const categoryCounts: { [key: string]: number } = {};
    comparisonData.forEach((row) => {
      categoryCounts[row.category] = (categoryCounts[row.category] || 0) + 1;
    });
    let currentCategory = '';
    const categories = Array.from(new Set(comparisonData.map((row) => row.category)));
    const lastCategory = categories[categories.length - 1];
    comparisonData.forEach((row) => {
      const isFirstInCategory = row.category !== currentCategory;
      if (isFirstInCategory) currentCategory = row.category;
      out.push({
        ...row,
        categoryRowspan: isFirstInCategory ? categoryCounts[row.category] : undefined,
        isFirstInCategory,
        isLastCategory: row.category === lastCategory && isFirstInCategory,
      });
    });
    return out;
  }, []);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>이용권 플랜 안내</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>
        <ModalBody>
          <TopNotice>
            이용권 구입은 로그인 후
            <br />
            이용권 구매 및 관리 페이지를 이용해주세요
          </TopNotice>

          <RefundInfoSection>
            <RefundInfoTitle>{purchaseMode === 'group' ? '그룹 이용권 구입' : '이용권 구입'}</RefundInfoTitle>
            <RefundInfoMessage>
              {purchaseMode === 'group' ? (
                <>
                  그룹 이용권은 구매일로부터 7일 이내에 한하여 구매자가 환불을 신청할 수 있습니다.
                  <br />
                  환불 시 미활성 이용권은 전액 환불되나, 활성화된 이용권은 10%의 운영 수수료와 사용일수만큼 차감 후 잔액이 지급됩니다.
                  <br />
                  그룹 이용권 특성상 부분 취소는 불가능하며 전체 수량이 일괄 취소됩니다.
                  <br />
                  또한, 모든 코드는 구매 후 한 달 이내에만 활성화할 수 있으므로,
                  <br />
                  미사용 권한이 소멸되지 않도록 기간 내 등록을 완료해 주시기 바랍니다.
                </>
              ) : (
                <>
                  이용권(미활성/활성 공통): 구매일로부터 7일 이내에만 환불 가능.
                  <br />
                  환불 시 취소·운영 수수료 10%와 해당 이용권 결제금액의 일금액×사용일수(오늘 포함)를 제외한 잔액이 환불됩니다.
                  <br />
                  이용권 이용기간은 이용권 쿠폰 등록 또는 이용권 구매 즉시 적용됩니다.
                  <br />
                  1개월 플랜에서 1년 플랜으로 업그레이드 할 시 기존 유료 플랜 이용 시작일 부터 1년 동안을 이용 기간으로 업그레이드 됩니다.
                </>
              )}
            </RefundInfoMessage>
          </RefundInfoSection>

          <PurchaseModeToggleContainer>
            <PurchaseModeToggleButton $isActive={purchaseMode === 'single'} onClick={() => setPurchaseMode('single')}>
              단일 이용자 구매
            </PurchaseModeToggleButton>
            <PurchaseModeToggleButton $isActive={purchaseMode === 'group'} onClick={() => setPurchaseMode('group')}>
              그룹 이용자 구매
            </PurchaseModeToggleButton>
          </PurchaseModeToggleContainer>

          <TabContainer>
            <TabButton $isActive={selectedDuration === 1} onClick={() => setSelectedDuration(1)}>
              {purchaseMode === 'group' ? '그룹 N개월 이용권' : 'N개월 이용권'}
            </TabButton>
            <TabButton $isActive={selectedDuration === 12} onClick={() => setSelectedDuration(12)}>
              {purchaseMode === 'group' ? '그룹 1년 이용권' : '1년 이용권'}
            </TabButton>
          </TabContainer>

          {sortedPlansByDuration[selectedDuration] && sortedPlansByDuration[selectedDuration].length > 0 && (
            <CategorySection>
              <PlanRow>
                {sortedPlansByDuration[selectedDuration].map((plan) => {
                  const isYearPlan = plan.duration === 12;
                  const oneYearDiscount = isYearPlan && plan.originalPrice != null && plan.originalPrice > plan.price && (
                    <>
                      <PlanPriceOriginalRow>
                        <PlanPriceLabel>연</PlanPriceLabel>
                        <PlanPriceStrikethrough>{licenseService.formatPrice(plan.originalPrice, plan.currency)}</PlanPriceStrikethrough>
                      </PlanPriceOriginalRow>
                      <PlanPriceDivider />
                    </>
                  );
                  const priceEl = isYearPlan ? (
                    <PlanPrice>
                      <PlanPriceLabel>할인가</PlanPriceLabel>
                      {licenseService.formatPrice(plan.price, plan.currency)}
                    </PlanPrice>
                  ) : (
                    <PlanPrice>
                      <PlanPriceLabel>월</PlanPriceLabel>
                      {licenseService.formatPrice(plan.price, plan.currency)}
                    </PlanPrice>
                  );
                  return (
                    <PlanCard key={plan.id} $isCurrent={false}>
                      <PlanTypeName>{getPlanTypeName(plan.planType)}</PlanTypeName>
                      {getPlanSubtitle(plan.planType) && (
                        <PlanSubtitle>{getPlanSubtitle(plan.planType)}</PlanSubtitle>
                      )}
                      <PlanPriceContainer>
                        {oneYearDiscount}
                        {priceEl}
                        {plan.planType !== 'free' ? (
                          <PlanBadge>{purchaseMode === 'group' ? '그룹 구매' : '이용권 구입'}</PlanBadge>
                        ) : (
                          <PlanBadgeSpacer aria-hidden />
                        )}
                      </PlanPriceContainer>
                      <PlanDetails>
                        {groupFeaturesByCategory(plan.planType).map((categoryGroup, categoryIndex) => (
                          <FeatureCategoryGroup key={categoryIndex}>
                            <CategoryLabel>{categoryGroup.category}</CategoryLabel>
                            {categoryGroup.features.map((feature, featureIndex) => (
                              feature ? (
                                <PlanDetailItem key={featureIndex}>{feature}</PlanDetailItem>
                              ) : (
                                <PlanDetailItem key={featureIndex} style={{ minHeight: '1.4em' }}>&nbsp;</PlanDetailItem>
                              )
                            ))}
                          </FeatureCategoryGroup>
                        ))}
                      </PlanDetails>
                    </PlanCard>
                  );
                })}
              </PlanRow>
            </CategorySection>
          )}

          <ComparisonSection>
            <ComparisonTitle>플랜 비교</ComparisonTitle>
            <ComparisonTable>
              <TableHead>
                <TableRow>
                  <TableHeader>카테고리</TableHeader>
                  <TableHeader>기능</TableHeader>
                  <TableHeader>프리</TableHeader>
                  <TableHeader>베이직</TableHeader>
                  <TableHeader>스탠다드</TableHeader>
                  <TableHeader>프로</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedData.map((row, index) => (
                  <TableRow key={index}>
                    {row.isFirstInCategory && row.categoryRowspan ? (
                      <CategoryCell rowSpan={row.categoryRowspan} $isLastCategory={row.isLastCategory}>
                        {row.category}
                      </CategoryCell>
                    ) : null}
                    <TableData $isFeature>
                      {row.feature.split('\n').map((line, idx) => (
                        <React.Fragment key={idx}>
                          {line}
                          {idx < row.feature.split('\n').length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </TableData>
                    <TableData>
                      {row.free === '가능' || row.free === '불가' ? (
                        <FeatureStatusBadge $isAvailable={row.free === '가능'}>
                          {row.free === '가능' ? (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                              <circle cx="12" cy="12" r="9" stroke="currentColor" fill="none" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            </svg>
                          )}
                        </FeatureStatusBadge>
                      ) : (
                        row.free
                      )}
                    </TableData>
                    <TableData>
                      {row.basic === '가능' || row.basic === '불가' ? (
                        <FeatureStatusBadge $isAvailable={row.basic === '가능'}>
                          {row.basic === '가능' ? (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                              <circle cx="12" cy="12" r="9" stroke="currentColor" fill="none" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            </svg>
                          )}
                        </FeatureStatusBadge>
                      ) : (
                        row.basic
                      )}
                    </TableData>
                    <TableData>
                      {row.standard === '가능' || row.standard === '불가' ? (
                        <FeatureStatusBadge $isAvailable={row.standard === '가능'}>
                          {row.standard === '가능' ? (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                              <circle cx="12" cy="12" r="9" stroke="currentColor" fill="none" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            </svg>
                          )}
                        </FeatureStatusBadge>
                      ) : (
                        row.standard
                      )}
                    </TableData>
                    <TableData>
                      {row.pro === '가능' || row.pro === '불가' ? (
                        <FeatureStatusBadge $isAvailable={row.pro === '가능'}>
                          {row.pro === '가능' ? (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                              <circle cx="12" cy="12" r="9" stroke="currentColor" fill="none" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            </svg>
                          )}
                        </FeatureStatusBadge>
                      ) : (
                        row.pro
                      )}
                    </TableData>
                  </TableRow>
                ))}
              </TableBody>
            </ComparisonTable>
          </ComparisonSection>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default PublicLicensePlanModal;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 12000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalContent = styled.div`
  width: 96%;
  max-width: 1280px;
  max-height: 94vh;
  overflow-y: auto;
  background: var(--color-surface);
  border-radius: 12px;
  border: var(--modal-border);
  box-shadow: var(--modal-shadow-large);
`;

const ModalHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-surface);
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text-primary);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 32px;
  cursor: pointer;
  color: var(--color-text-gray);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition: color 0.2s ease;
  &:hover {
    color: var(--color-text-primary);
  }
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const TopNotice = styled.div`
  text-align: center;
  color: var(--color-accent-strong);
  font-size: 18px;
  font-weight: 800;
  line-height: 1.5;
  background: rgba(var(--color-accent-rgb), 0.08);
  border: 1px solid rgba(var(--color-accent-rgb), 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid var(--color-border);
  margin-bottom: 24px;
  flex-shrink: 0;
`;

const PurchaseModeToggleContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  justify-content: center;
`;

const PurchaseModeToggleButton = styled.button<{ $isActive: boolean }>`
  border: 1px solid ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  background: ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-surface)'};
  color: ${props => props.$isActive ? '#fff' : 'var(--color-text-primary)'};
  border-radius: 999px;
  padding: 8px 14px;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    opacity: 0.9;
  }
`;

const RefundInfoSection = styled.div`
  margin-top: 0;
  margin-bottom: 24px;
`;

const RefundInfoTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0 0 24px 0;
  text-align: center;
`;

const RefundInfoMessage = styled.div`
  text-align: center;
  margin-top: 0;
  margin-bottom: 16px;
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  line-height: 1.5;
  padding: 8px 16px;
  background: var(--color-panel);
  border-radius: 8px;
`;

const TabButton = styled.button<{ $isActive: boolean }>`
  flex: 1;
  padding: 16px 24px;
  border: none;
  color: ${props => props.$isActive ? 'white' : 'var(--color-text-secondary)'};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 0;
  background: ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-panel)'};
  :root[data-theme='light'] & {
    background: ${props => props.$isActive ? 'var(--color-accent-strong)' : '#F0F0F0'};
  }
  :root:not([data-theme='light']) & {
    background: ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-surface)'};
  }
  &:first-child {
    border-top-left-radius: 8px;
  }
  &:last-child {
    border-top-right-radius: 8px;
  }
  &:hover {
    background: ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  }
`;

const CategorySection = styled.div`
  margin-bottom: 32px;
`;

const PlanRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const PlanCard = styled.div<{ $isCurrent: boolean }>`
  width: 100%;
  min-height: 280px;
  background: var(--color-surface);
  border: 2px solid ${props => props.$isCurrent ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  border-radius: 12px;
  padding: 24px 16px 16px 16px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
`;

const PlanTypeName = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: 4px;
  text-align: center;
  flex-shrink: 0;
`;

const PlanSubtitle = styled.div`
  font-size: 11px;
  color: #999;
  text-align: center;
  margin-top: 0;
  margin-bottom: 12px;
  flex-shrink: 0;
  min-height: 2.6em;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1.3;
  padding: 0 4px;
  :root:not([data-theme='light']) & {
    color: #888;
  }
`;

const PlanPriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-bottom: 12px;
  flex-shrink: 0;
`;

const PlanPrice = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: var(--color-accent-strong);
  text-align: center;
  :root:not([data-theme='light']) & {
    color: white;
  }
`;

const PlanPriceOriginalRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  text-align: center;
`;

const PlanPriceStrikethrough = styled.span`
  font-size: 24px;
  font-weight: 700;
  color: var(--color-text-secondary);
  text-decoration: line-through;
  opacity: 0.6;
  :root:not([data-theme='light']) & {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const PlanPriceDivider = styled.div`
  width: 100%;
  max-width: 80px;
  height: 0;
  border-top: 1px solid var(--color-border);
  margin: 2px 0;
  :root:not([data-theme='light']) & {
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const PlanPriceLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-secondary);
  margin-right: 2px;
  :root:not([data-theme='light']) & {
    color: rgba(255, 255, 255, 0.7);
  }
`;

const PlanBadge = styled.span`
  display: inline-block;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-accent-strong);
  background: var(--color-background);
  border: 1px solid var(--color-accent-strong);
  margin-top: 4px;
  :root:not([data-theme='light']) & {
    color: white;
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const PlanBadgeSpacer = styled.span`
  display: inline-block;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  margin-top: 4px;
  visibility: hidden;
  pointer-events: none;
  box-sizing: border-box;
  line-height: 1.2;
  &::after {
    content: '이용권 구입';
  }
`;

const PlanDetails = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: auto;
  padding-top: 12px;
`;

const FeatureCategoryGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 4px;
  &:not(:last-child) {
    border-bottom: 1px solid var(--color-border);
    padding-bottom: 8px;
    margin-bottom: 8px;
  }
`;

const CategoryLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: var(--color-accent-strong);
  text-align: left;
  margin-bottom: 6px;
  padding-left: 4px;
  min-height: 14px;
  :root:not([data-theme='light']) & {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const PlanDetailItem = styled.div`
  font-size: 11px;
  color: var(--color-text-secondary);
  text-align: left;
  line-height: 1.4;
  word-break: keep-all;
  white-space: pre-line;
  padding-left: 8px;
  min-height: 1.4em;
  display: block;
  &:not(:last-child) {
    margin-bottom: 4px;
  }
`;

const ComparisonSection = styled.div`
  margin-top: 48px;
  margin-bottom: 0;
`;

const ComparisonTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0 0 24px 0;
  text-align: center;
`;

const ComparisonTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: var(--color-surface);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const TableHead = styled.thead`
  background: var(--color-accent-strong);
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid var(--color-border);
  &:last-child {
    border-bottom: none;
  }
`;

const TableHeader = styled.th`
  padding: 16px;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  color: white;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  &:last-child {
    border-right: none;
  }
`;

const TableData = styled.td<{ $isFeature?: boolean }>`
  padding: 16px;
  text-align: center;
  font-size: 13px;
  color: var(--color-text-secondary);
  border-right: 1px solid var(--color-border);
  vertical-align: middle;
  &:last-child {
    border-right: none;
  }
  ${props => props.$isFeature && `
    font-weight: 600;
    color: var(--color-text-primary);
    text-align: left;
    background: var(--color-panel);
    white-space: pre-line;
    line-height: 1.5;
    :root[data-theme='light'] & {
      background: #ffffff;
    }
    :root:not([data-theme='light']) & {
      background: var(--color-surface);
    }
  `}
`;

const CategoryCell = styled.td<{ $isLastCategory?: boolean }>`
  padding: 16px;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  color: var(--color-text-primary);
  background: var(--color-panel);
  border-right: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  vertical-align: middle;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  width: 60px;
  :root[data-theme='light'] & {
    background: #ffffff;
  }
  :root:not([data-theme='light']) & {
    background: var(--color-surface);
  }
  ${props => props.$isLastCategory && `
    border-bottom: none;
  `}
`;

const FeatureStatusBadge = styled.div<{ $isAvailable: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ $isAvailable }) => ($isAvailable ? '#10504a' : '#ff6b6b')};
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
  overflow: visible;
  padding: 2px;

  svg {
    width: 14px;
    height: 14px;
    color: white;
    display: block;
    overflow: visible;
  }
`;
